import { Col, Row } from 'antd';
import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FinanceProductBasedOnField } from '.';
import { FinanceProductBasedOn } from '../../../../../api/types';
import { CommonType } from '../../shared/typings';
import { type FinanceProductFormValues } from '../index';
import DownPayment from './DownPayment';
import Loan from './Loan';
import YesNoField from './YesNoField';

const colSpan = { lg: 8, xs: 24 };

const DownPaymentOrLoan = ({ disabled: disabledFromPermission = false }: { disabled?: boolean }) => {
    const { t } = useTranslation(['financeProductDetails']);

    const { values } = useFormikContext<FinanceProductFormValues>();

    const { basedOn, downPayment, loan } = values;

    const disabled = useMemo(
        () => ({
            downPayment: {
                isEditable: !downPayment?.isViewable || basedOn === FinanceProductBasedOn.DownPayment,
            },
            loan: { isEditable: !loan?.isViewable || basedOn === FinanceProductBasedOn.Loan },
        }),
        [basedOn, downPayment?.isViewable, loan?.isViewable]
    );

    return (
        <Row gutter={[16, 16]}>
            <Col span={24}>
                <FinanceProductBasedOnField disabled={disabledFromPermission} />
            </Col>
            {basedOn === FinanceProductBasedOn.DownPayment ? (
                <DownPayment disabled={disabledFromPermission} />
            ) : (
                <Loan disabled={disabledFromPermission} />
            )}

            <Col {...colSpan}>
                <YesNoField
                    disabled={disabledFromPermission}
                    {...t('financeProductDetails:fields.downPaymentViewable', { returnObjects: true })}
                    name="downPayment.isViewable"
                />
            </Col>
            {downPayment?.type === CommonType.Range && (
                <Col {...colSpan}>
                    <YesNoField
                        {...t('financeProductDetails:fields.downPaymentEditable', { returnObjects: true })}
                        disabled={disabled.downPayment.isEditable || disabledFromPermission}
                        name="downPayment.isEditable"
                    />
                </Col>
            )}
            <Col {...colSpan}>
                <YesNoField
                    {...t('financeProductDetails:fields.loanViewable', { returnObjects: true })}
                    disabled={disabledFromPermission}
                    name="loan.isViewable"
                />
            </Col>
            {downPayment?.type === CommonType.Range && (
                <Col {...colSpan}>
                    <YesNoField
                        {...t('financeProductDetails:fields.loanEditable', { returnObjects: true })}
                        disabled={disabled.loan.isEditable || disabledFromPermission}
                        name="loan.isEditable"
                    />
                </Col>
            )}
        </Row>
    );
};

export default DownPaymentOrLoan;
